<script setup lang="ts">
import { Check } from 'lucide-vue-next'
import type { ListFilterCriterion } from '@/types/listFilters'

const props = defineProps<{
  criterion: ListFilterCriterion
}>()

const { t } = useI18n()

const search = ref('')

const searchPlaceholder = computed(() => props.criterion.searchPlaceholder ?? t('listFilters.search'))

const visibleOptions = computed(() => {
  const query = search.value.trim().toLowerCase()
  if (!query) return props.criterion.options
  return props.criterion.options.filter((option) => option.label.toLowerCase().includes(query))
})

const isSelected = (value: string) => props.criterion.selected.includes(value)

const onToggle = (value: string) => {
  if (!props.criterion.multiple && isSelected(value)) {
    props.criterion.clear()
    return
  }
  props.criterion.toggle(value)
}
</script>

<template>
  <div v-if="criterion.searchable" class="border-b p-1">
    <Input
      v-model="search"
      class="h-8"
      :placeholder="searchPlaceholder"
      :aria-label="searchPlaceholder"
      @keydown.stop
    />
  </div>
  <div class="max-h-64 overflow-y-auto p-1">
    <DropdownMenuCheckboxItem
      v-for="option in visibleOptions"
      :key="option.value"
      class="group pl-2"
      :model-value="isSelected(option.value)"
      :data-testid="`list-filter-option-${criterion.key}-${option.value}`"
      @update:model-value="onToggle(option.value)"
      @select.prevent
    >
      <!-- The native indicator only renders once checked: the box follows the item state to show unchecked too -->
      <template #indicator-icon>
        <span />
      </template>
      <span
        aria-hidden="true"
        class="grid size-4 shrink-0 place-content-center rounded-sm border border-input bg-background text-primary-foreground shadow-xs group-data-[state=checked]:border-primary group-data-[state=checked]:bg-primary"
      >
        <Check class="size-3.5 opacity-0 group-data-[state=checked]:opacity-100" />
      </span>
      <span class="min-w-0 flex-1 truncate">{{ option.label }}</span>
      <span v-if="option.hint" class="max-w-28 shrink-0 truncate text-xs text-muted-foreground">
        {{ option.hint }}
      </span>
      <span v-if="option.count != null" class="text-xs tabular-nums text-muted-foreground">{{ option.count }}</span>
    </DropdownMenuCheckboxItem>
    <p v-if="!visibleOptions.length" class="px-2 py-3 text-center text-sm text-muted-foreground">
      {{ criterion.emptyText ?? t('listFilters.noResult') }}
    </p>
  </div>
</template>
